<template>
  <div id="projects-supplier-detail-view">
    <PVTabs v-model:value="activeTab" lazy>
      <PVTabsList>
        <router-link :to="getTabLink(ProjectsSupplierDetailViewTab.COWORKERS)">
          <PVTab :value="ProjectsSupplierDetailViewTab.COWORKERS">
            {{ $t('supplierDetailView.coworkers') }}
          </PVTab>
        </router-link>
        <router-link
          :to="getTabLink(ProjectsSupplierDetailViewTab.CONTENT_TYPES)"
        >
          <PVTab :value="ProjectsSupplierDetailViewTab.CONTENT_TYPES">
            {{ $t('supplierDetailView.contentTypes') }}
          </PVTab>
        </router-link>
        <router-link :to="getTabLink(ProjectsSupplierDetailViewTab.CHECK_BOT)">
          <PVTab :value="ProjectsSupplierDetailViewTab.CHECK_BOT">
            {{ $t('supplierDetailView.checkBot') }}
          </PVTab>
        </router-link>
      </PVTabsList>
      <PVTabsPanels>
        <PVTabPanel :value="ProjectsSupplierDetailViewTab.COWORKERS">
          <projects-coworkers
            :project-id="projectId"
            :supplier-id="supplierId"
            :mode="projectCoworkersTableMode"
          ></projects-coworkers>
        </PVTabPanel>
        <PVTabPanel :value="ProjectsSupplierDetailViewTab.CONTENT_TYPES">
          <content-type-management-table
            :project-id="projectId"
            :supplier-id="supplierId"
          ></content-type-management-table>
        </PVTabPanel>
        <PVTabPanel :value="ProjectsSupplierDetailViewTab.CHECK_BOT">
          <check-bot-configs-management
            :project-id="projectId"
            :supplier-id="supplierId"
          ></check-bot-configs-management>
        </PVTabPanel>
      </PVTabsPanels>
    </PVTabs>
  </div>
</template>

<script lang="ts" setup>
  import { ref, onMounted } from 'vue';
  import { RouteLocationRaw } from 'vue-router';
  import { useToast } from '@/composables/useToast';
  import { IProject, ISupplier, ProjectType } from 'types';
  import axios, { AxiosResponse } from 'axios';
  import endpointsList from '@/helpers/endpointsList';
  import { Route, routeParams } from '@/routes';
  import { ProjectsSupplierDetailViewTab } from './ProjectsSupplierDetailView.types';
  import { useTabs } from '@/composables/useTabs';

  const { createErrorToast } = useToast();

  const props = defineProps<{
    projectId: string;
    projectData?: IProject;
    supplierId: string;
  }>();

  const fetchedProjectData = ref<IProject | undefined>(undefined);
  const supplierData = ref<ISupplier | undefined>(undefined);
  const projectCoworkersTableMode = ref<ProjectType>(
    ProjectType.SUPPLIER_PROJECT,
  );

  const { activeTab } = useTabs(
    Route.PROJECT_SUPPLIER_DETAILS,
    ProjectsSupplierDetailViewTab,
    ProjectsSupplierDetailViewTab.COWORKERS,
  );
  function getTabLink(tab: ProjectsSupplierDetailViewTab): RouteLocationRaw {
    return {
      name: Route.PROJECT_SUPPLIER_DETAILS,
      params: {
        [routeParams[Route.PROJECT_SUPPLIER_DETAILS].projectId]:
          props.projectId,
        [routeParams[Route.PROJECT_SUPPLIER_DETAILS].supplierId]:
          props.supplierId,
        [routeParams[Route.PROJECT_SUPPLIER_DETAILS].tab]: tab,
      },
    };
  }

  async function getProjectData() {
    if (props.projectData) return;

    try {
      const response: AxiosResponse<IProject> = await axios.get(
        endpointsList.projects.getProjectById(props.projectId),
      );
      fetchedProjectData.value = response.data;
    } catch (error) {
      createErrorToast('Failed to fetch project data.');
    }
  }

  async function getSupplierData() {
    try {
      const response: AxiosResponse<ISupplier> = await axios.get(
        endpointsList.suppliers.getSupplierById(props.supplierId),
      );
      supplierData.value = response.data;
    } catch (error) {
      createErrorToast('Failed to fetch supplier data.');
    }
  }

  onMounted(() => {
    if (!props.projectData) {
      getProjectData();
    }
    getSupplierData();
  });
</script>
